/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Logger } from '../logger/logger';

/**
 * Validates if a string is a valid URL
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Options for API calls
 */
export type FetchClientOptions = {
  body?: any;
  headers?: Record<string, string>;
  keepalive?: boolean;
  method: string;
  params?: Record<string, string>;
  retries?: number;
  retryAfter?: number;
  url: string;
};

/**
 * Response from API calls
 */
export type FetchClientResponse<T = any> = {
  _meta?: Record<string, any>;
  data: T;
  error?: string;
  ok: boolean;
  status?: number;
};

/**
 * Builds a URL object from a string URL and optional parameters
 */
export const buildUrl = (
  url: string,
  params?: Record<string, string>,
  baseUrl?: string,
  useRelativeUrl?: boolean,
): URL | null => {
  let urlObj: URL;

  try {
    if (isValidUrl(url)) {
      // If the incoming URL is valid
      urlObj = new URL(url);
    } else if (
      useRelativeUrl &&
      typeof location !== 'undefined' &&
      isValidUrl(location.origin + url)
    ) {
      // If useRelativeUrl is true and the URL is a relative URL
      urlObj = new URL(location.origin + url);
    } else if (baseUrl && isValidUrl(baseUrl + url)) {
      urlObj = new URL(baseUrl + url);
    } else {
      // If the URL is invalid or other conditions are not met, return null
      return null;
    }

    // Append parameters if they exist
    if (params) {
      Object.entries(params).forEach(([key, value]) =>
        urlObj.searchParams.append(key, value),
      );
    }

    return urlObj;
  } catch (error) {
    Logger.error('Error building URL', { baseUrl, error, url });
    return null;
  }
};

/**
 * Prepares request options for fetch
 */
export const prepareOptions = (
  method: string,
  body?: any,
  headers?: Record<string, string>,
  keepalive?: boolean,
): RequestInit => {
  const options: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    method,
  };

  if (body !== undefined) {
    options.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  if (keepalive) {
    options.keepalive = true;
  }

  return options;
};

/**
 * Base fetch client function to make API calls with retry logic
 */
export async function baseFetchClient<T = any>(
  options: FetchClientOptions,
  baseUrl?: string,
  useRelativeUrl?: boolean,
): Promise<FetchClientResponse<T>> {
  const {
    body,
    headers,
    keepalive = true,
    method,
    params,
    retries = 3,
    retryAfter = 1000,
    url,
  } = options;

  const urlObj = buildUrl(url, params, baseUrl, useRelativeUrl);
  if (!urlObj) {
    Logger.warn('Invalid URL', { baseUrl, url, useRelativeUrl });
    return {
      data: {} as T,
      error: 'Invalid URL',
      ok: false,
    };
  }

  const requestOptions = prepareOptions(method, body, headers, keepalive);
  let retryCount = 0;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      Logger.debug(`Making ${method} request to: ${urlObj.toString()}`);

      const response = await fetch(urlObj.toString(), requestOptions);
      let responseData;

      try {
        responseData = await response.json();
      } catch {
        // Ignore JSON parsing errors
        responseData = {};
      }

      const result: FetchClientResponse<T> = {
        data: responseData,
        ok: response.ok,
        status: response.status,
      };

      // Add retry metadata if applicable
      if (retryCount > 0) {
        result._meta = { retryCount };
      }

      return result;
    } catch (error) {
      retryCount++;

      if (attempt < retries - 1) {
        // Wait before retrying with exponential backoff
        await new Promise(resolve =>
          setTimeout(resolve, retryAfter * Math.pow(2, attempt)),
        );
      } else {
        Logger.error('API call failed after retries', {
          error,
          retries,
          url: urlObj.toString(),
        });

        return {
          data: {} as T,
          error: error instanceof Error ? error.message : String(error),
          ok: false,
        };
      }
    }
  }

  // This should never be reached due to the final else in the try/catch
  return {
    data: {} as T,
    error: 'Max retries reached',
    ok: false,
  };
}

/**
 * The main callAPI function - directly calls baseFetchClient
 */
export async function callAPI<T = any>(
  options: FetchClientOptions,
  baseUrl?: string,
  useRelativeUrl?: boolean,
): Promise<FetchClientResponse<T>> {
  return baseFetchClient<T>(options, baseUrl, useRelativeUrl);
}
